import { InsuranceProvider } from '@coolblue-development/next-insurance/insurance-selector';
import {
  CartItemRelation,
  PricedShoppingCartItem,
  PricedShoppingCartSubItem
} from '@coolblue-development/next-shopping-cart/actions';
import {
  EnrichedShoppingCartItem,
  MappedShoppingCartItem
} from '@features/add-to-cart-page/typings/ShoppingCart';

export const isProductInCart = (shoppingCartItems: PricedShoppingCartItem[], productId: number) => {
  return !!getShoppingCartItemFromProductId(shoppingCartItems, productId)?.id;
};

export const getShoppingCartItemFromProductId = (
  shoppingCartItems: EnrichedShoppingCartItem[],
  productId?: number
) => {
  if (!productId) {
    return;
  }

  const selectedShoppingCartItem = shoppingCartItems
    .sort((a, b) => new Date(b.modificationDate).getTime() - new Date(a.modificationDate).getTime())
    .find(item => Number(item.productId) === productId);

  if (!selectedShoppingCartItem) {
    return;
  }

  return mapShoppingCartItem(selectedShoppingCartItem);
};

const mapShoppingCartItem = (item: EnrichedShoppingCartItem): MappedShoppingCartItem => {
  const selectedInsurance = (item.subItems as PricedShoppingCartSubItem[]).find(
    subItem =>
      subItem.shoppingCartItemCategory === 'insurance' ||
      subItem.shoppingCartItemCategory === 'insurancePartner'
  );

  const installationServices = (item.subItems as PricedShoppingCartSubItem[]).filter(
    subItem =>
      subItem.shoppingCartItemCategory === 'installationService' ||
      subItem.shoppingCartItemCategory === 'serviceAccessory'
  );

  return {
    id: item.shoppingCartItemId,
    modificationDate: item.modificationDate,
    category: item.shoppingCartItemCategory,
    product: item.product,
    installationServices: installationServices.map(subItem => ({
      id: subItem.productId,
      price: {
        includingVat: subItem.shoppingCartItemPrice.includingVat,
        excludingVat: subItem.shoppingCartItemPrice.excludingVat
      }
    })),
    subItems: (item.subItems as PricedShoppingCartSubItem[]).map(subItem => ({
      id: subItem.shoppingCartItemId,
      productId: subItem.productId,
      price: {
        includingVat: subItem.shoppingCartItemPrice.includingVat,
        excludingVat: subItem.shoppingCartItemPrice.excludingVat
      }
    })),
    insurance: selectedInsurance && {
      subItemId: selectedInsurance.shoppingCartItemId,
      productId: selectedInsurance.productId,
      quoteId: selectedInsurance.relations?.[0]?.relatesTo?.[0],
      price: {
        includingVat: selectedInsurance.shoppingCartItemPrice.includingVat,
        excludingVat: selectedInsurance.shoppingCartItemPrice.excludingVat
      },
      provider:
        selectedInsurance.shoppingCartItemCategory === 'insurancePartner'
          ? InsuranceProvider.CoverGenius
          : InsuranceProvider.ERGO
    },
    quantity: item.quantity
  };
};

export function getConfiguratorShoppingCartItems(
  productId: string,
  shoppingCartItems: EnrichedShoppingCartItem[]
): EnrichedShoppingCartItem[] {
  return shoppingCartItems.filter((item: EnrichedShoppingCartItem) =>
    item.relations?.some(
      (relation: CartItemRelation) =>
        relation.relationType === 'discount' &&
        relation.discountReason === 'CONFIGURATOR' &&
        relation.relatesTo.includes(productId) &&
        (relation.discountExpiresAt ? new Date() < new Date(relation.discountExpiresAt) : true)
    )
  );
}
